/**
 * Dashboard Menu Configuration
 * Based on the current OptraAI MVP dashboard scope
 */

export type Role = 'owner' | 'admin' | 'member' | 'viewer'

export interface MenuItem {
  id: string
  label: string
  icon?: string // Lucide icon name
  route?: string
  external?: boolean
  collapsible?: boolean
  defaultOpen?: boolean
  roles: Role[]
  items?: MenuItem[]
  separator?: boolean // Add a visual separator before this item
}

// Workspace-Scoped Menu (Top Section) - Focused on MVP governance workflows
export const workspaceMenu: MenuItem[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: 'LayoutDashboard',
    route: '/dashboard',
    collapsible: false,
    roles: ['owner', 'admin', 'member', 'viewer'],
  },
  {
    id: 'gateway',
    label: 'Gateway',
    icon: 'Waypoints',
    route: '/gateway',
    collapsible: true,
    defaultOpen: true,
    roles: ['owner', 'admin', 'member', 'viewer'],
    items: [
      {
        id: 'gateway-overview',
        label: 'Configurations',
        icon: 'Settings',
        route: '/gateway',
        roles: ['owner', 'admin', 'member', 'viewer'],
      },
      {
        id: 'provider-health',
        label: 'Provider Health',
        icon: 'Activity',
        route: '/gateway/providers',
        roles: ['owner', 'admin', 'member', 'viewer'],
      },
      {
        id: 'gateway-performance',
        label: 'Performance',
        icon: 'Gauge',
        route: '/gateway/performance',
        roles: ['owner', 'admin', 'member', 'viewer'],
      },
      {
        id: 'log-analysis',
        label: 'Log Analysis',
        icon: 'FileSearch',
        roles: ['owner', 'admin', 'member', 'viewer'],
        items: [
          {
            id: 'request-logs',
            label: 'Request Logs',
            icon: 'ScrollText',
            route: '/gateway/logs',
            roles: ['owner', 'admin', 'member', 'viewer'],
          },
          {
            id: 'monitoring',
            label: 'Monitoring',
            icon: 'MonitorDot',
            route: '/gateway/monitoring',
            roles: ['owner', 'admin', 'member', 'viewer'],
          },
          {
            id: 'usage-alerts',
            label: 'Alerts',
            icon: 'BellRing',
            route: '/gateway/alerts',
            roles: ['owner', 'admin', 'member', 'viewer'],
          },
        ],
      },
      {
        id: 'security',
        label: 'Security',
        icon: 'ShieldCheck',
        roles: ['owner', 'admin', 'member', 'viewer'],
        items: [
          // Guardrails is temporarily hidden from the MVP menu: it pointed at
          // the '/gateway' placeholder (identical to Gateway > Configurations)
          // because no dedicated Guardrails page exists yet. Re-enable once a
          // standalone Guardrails route/page is built. Introduced in d1bd3b03.
          {
            id: 'violations',
            label: 'Violations',
            icon: 'ShieldAlert',
            route: '/gateway/violations',
            roles: ['owner', 'admin', 'member', 'viewer'],
          },
          {
            id: 'data-retention',
            label: 'Data Retention',
            icon: 'DatabaseBackup',
            route: '/organization/[orgId]/settings/data-retention',
            roles: ['owner', 'admin', 'member', 'viewer'],
          },
        ],
      },
    ],
  },
  {
    id: 'workspace-costs',
    label: 'Cost Analysis',
    icon: 'DollarSign',
    collapsible: true,
    defaultOpen: true,
    roles: ['owner', 'admin', 'member', 'viewer'],
    items: [
      {
        id: 'cost-dashboard',
        label: 'Cost Dashboard',
        route: '/WorkspaceCost/cost',
        icon: 'LayoutDashboard',
        roles: ['owner', 'admin', 'member', 'viewer'],
      },
      {
        id: 'token-analytics',
        label: 'Token Analytics',
        route: '/WorkspaceCost/tokens',
        icon: 'BarChart3',
        roles: ['owner', 'admin', 'member', 'viewer'],
      },
      {
        id: 'usage-trends',
        label: 'Usage Trends',
        route: '/WorkspaceCost/trends',
        icon: 'LineChart',
        roles: ['owner', 'admin', 'member', 'viewer'],
      },
      // Savings menu intentionally hidden — page is not yet developed.
    ],
  },
  {
    // FO-UI21: FinOps (cost governance) group, between Cost Analysis and Model
    // Labs. Holds cost breakdown/export, budget, forecast, and cost tags.
    id: 'finops',
    label: 'FinOps',
    icon: 'Coins',
    collapsible: true,
    defaultOpen: true,
    roles: ['owner', 'admin', 'member', 'viewer'],
    items: [
      {
        id: 'cost-breakdown',
        label: 'Cost Breakdown',
        route: '/WorkspaceCost/breakdown',
        icon: 'PieChart',
        roles: ['owner', 'admin', 'member', 'viewer'],
      },
      {
        // FO-UI20: dedicated Cost Export page (independent of Cost Breakdown).
        id: 'cost-export',
        label: 'Cost Export',
        route: '/WorkspaceCost/export',
        icon: 'Download',
        roles: ['owner', 'admin', 'member', 'viewer'],
      },
      {
        // FO-UI15: Budget only here (usage alerts live under Gateway > Alerts).
        // id kept as 'budget-alerts' for menu-test stability.
        id: 'budget-alerts',
        label: 'Budget',
        route: '/WorkspaceCost/budgets',
        icon: 'Wallet',
        roles: ['owner', 'admin', 'member', 'viewer'],
      },
      {
        id: 'cost-forecast',
        label: 'Forecast',
        route: '/WorkspaceCost/forecast',
        icon: 'TrendingUp',
        roles: ['owner', 'admin', 'member', 'viewer'],
      },
      {
        // FO-UI21: moved from Organization → FinOps (attribution = tags).
        id: 'cost-allocation',
        label: 'Cost Tags',
        icon: 'Tag',
        route: '/organization/[orgId]/cost-allocation',
        roles: ['owner', 'admin'],
      },
    ],
  },
  {
    id: 'model-lab',
    label: 'Model Labs',
    icon: 'FlaskConical',
    route: '/lab',
    collapsible: false,
    roles: ['owner', 'admin', 'member'],
  },
  {
    id: 'organization',
    label: 'Organization',
    icon: 'Building2',
    collapsible: true,
    defaultOpen: false,
    roles: ['owner', 'admin', 'member', 'viewer'],
    items: [
      {
        id: 'org-overview',
        label: 'Overview',
        icon: 'LayoutDashboard',
        route: '/organization/[orgId]/overview',
        roles: ['owner', 'admin', 'member', 'viewer'],
      },
      {
        id: 'workspace',
        label: 'Workspaces',
        icon: 'FolderKanban',
        route: '/workspace',
        roles: ['owner', 'admin'],
      },
      {
        id: 'users',
        label: 'Users',
        icon: 'Users',
        route: '/organization/[orgId]/users',
        roles: ['owner', 'admin'],
      },
      {
        id: 'llm-keys',
        label: 'BYOK',
        icon: 'Key',
        route: '/organization/[orgId]/llm-keys',
        roles: ['owner', 'admin'],
      },
      {
        id: 'management-keys',
        label: 'Management Keys',
        icon: 'KeyRound',
        route: '/organization/[orgId]/management-keys',
        roles: ['owner', 'admin'],
      },
      // FO-UI21: 'cost-allocation' (Cost Tags) moved to the FinOps group.
    ],
  },
]

// Organization-Scoped Menu (Deprecated - kept for backwards compatibility)
// Organization items are now part of workspaceMenu as a collapsible submenu
export const organizationMenu: MenuItem[] = []

/**
 * Recursively filter a menu tree to items the given role can see.
 *
 * Phase 1 RBAC §1.3: sidebar visibility is cosmetic — backend remains the
 * source of truth and returns 403 on direct URL navigation to restricted
 * pages. If `role` is undefined (e.g. during initial load before the user's
 * org membership has resolved), the unfiltered menu is returned to avoid a
 * blank sidebar.
 */
export function filterMenuByRole(
  items: MenuItem[],
  role: Role | undefined,
): MenuItem[] {
  if (!role) return items
  return items
    .filter((item) => item.roles.includes(role))
    .map((item) => ({
      ...item,
      items: item.items ? filterMenuByRole(item.items, role) : undefined,
    }))
}
